/**
 * How a segment is worded on screen — its counts, and since slice 50 what KIND
 * of segment it is.
 *
 * The counts themselves come from the API and are computed on every read; this
 * file decides only what each one is *called* and what it means, in one place,
 * so the list, the detail screen and the create preview cannot describe the
 * same number three different ways. {@link cohortProvenance} is here for the
 * same reason one level up: two screens draw a cohort's chip and its date, and
 * two hand-written versions of "Snapshot · Dormant audience · counted 26 Sep
 * 2026" would be two answers to *what is this segment*.
 *
 * The distinction the whole slice turns on:
 *
 *   contacts          rows in the union of the segment's lists
 *   uniqueRecipients  PEOPLE: the distinct persons those rows are about (slice
 *                     58), each counted once — the unit the send path reaches
 *                     once. Not a message count: a campaign sends one channel's
 *                     reach, below.
 *   emailReachable    distinct email addresses across those rows
 *   phoneReachable    distinct phone numbers the WhatsApp send would use — one
 *                     per person, their own number first
 *
 * The last two count **addresses, not rows carrying one**. A list that repeats
 * an address reaches that person once, so a row count would promise a reach the
 * send path then fails to deliver — the same failure the first two numbers exist
 * to prevent, one level down. They are therefore directly comparable with
 * `uniqueRecipients`, and never with `contacts`.
 *
 * 1,287 contacts that are 1,278 unique recipients is not a bug and must never
 * be presented as one: it is nine rows that are the same person twice. The
 * readings below exist so a screen can say that in words rather than leaving a
 * person to subtract two numbers and guess why they differ.
 */
import { OPPORTUNITY_COPY, type SegmentCohort, type SegmentCounts } from '@wend/shared';

import { formatDate } from './format';

/* -------------------------------------------------------------------------- */
/* Vocabulary                                                                  */
/* -------------------------------------------------------------------------- */

export type SegmentCountKey = keyof SegmentCounts;

export const SEGMENT_COUNT_LABEL: Readonly<Record<SegmentCountKey, string>> = {
  contacts: 'Contacts',
  uniqueRecipients: 'Unique recipients',
  // Shown under a "Reachable by channel" heading, so the label is the channel.
  emailReachable: 'Email',
  phoneReachable: 'WhatsApp',
};

/**
 * One line saying what the number is *for*. Never longer than a caption.
 *
 * The two reachability lines promise a send size, and are allowed to, because
 * they are counts of distinct addresses rather than of rows. This is the figure
 * a campaign on that channel will actually deliver — the wizard in slice 5 shows
 * the same number at the point the channel is chosen, and it must not move
 * between here and there.
 */
export const SEGMENT_COUNT_MEANING: Readonly<Record<SegmentCountKey, string>> = {
  contacts: 'Rows across the lists in this segment',
  uniqueRecipients: 'People, each counted once',
  emailReachable: 'People an email campaign would reach',
  phoneReachable: 'People a WhatsApp campaign would reach',
};

/* -------------------------------------------------------------------------- */
/* Readings                                                                    */
/* -------------------------------------------------------------------------- */

export interface SegmentCountReading {
  /**
   * Rows that are the same person as another row in the segment — on two lists,
   * or twice on one. `contacts - uniqueRecipients`, and the whole reason a
   * segment reports both numbers. Since slice 58 "the same person" is the stored
   * person (`contacts.person_id`), which can join two rows that share no address
   * inside this segment at all, through an upload outside it.
   */
  readonly duplicateRows: number;
  /**
   * People reachable *either* way — they have both an email address and a phone
   * number, so a campaign on either channel gets to them.
   *
   * Derived, by inclusion–exclusion over PEOPLE (slice 58): `emailReachable` is
   * the people with an email (an email has one owner), `phoneReachable` the
   * people with a phone, `uniqueRecipients` everybody — so the surplus is the
   * people carrying both.
   *
   *   bothChannels = emailReachable + phoneReachable − uniqueRecipients
   *
   * Exact while no two people share a phone number. When they do,
   * `phoneReachable` counts the number once for both, and this undercounts by
   * that much — towards zero, never past it.
   *
   * The denominator is `uniqueRecipients`, **not** `contacts`. All three are
   * counts of distinct things; subtracting a row count from address counts
   * mixes units and gives an answer that is wrong the moment a list repeats an
   * address. Clamped at zero anyway, so a future contract change can never
   * render this as a negative number.
   */
  readonly bothChannels: number;
  /** Nothing in the segment at all: no lists, or lists that imported nothing. */
  readonly empty: boolean;
  /** Every row is its own person — no address or number appears twice. */
  readonly allDistinct: boolean;
  /**
   * The two channels reach entirely different people. True of the first real
   * import, where one worksheet held email addresses and the other phone
   * numbers, and it is the reason a campaign must show the reach of the channel
   * it is actually sending on.
   */
  readonly channelsDisjoint: boolean;
}

export function readSegmentCounts(counts: SegmentCounts): SegmentCountReading {
  const duplicateRows = Math.max(0, counts.contacts - counts.uniqueRecipients);
  const bothChannels = Math.max(
    0,
    counts.emailReachable + counts.phoneReachable - counts.uniqueRecipients,
  );

  return {
    duplicateRows,
    bothChannels,
    empty: counts.contacts === 0,
    allDistinct: counts.contacts > 0 && duplicateRows === 0,
    channelsDisjoint:
      counts.contacts > 0 && bothChannels === 0 && counts.emailReachable > 0 && counts.phoneReachable > 0,
  };
}

/**
 * A share of the whole, 0–100, for the reachability bars.
 *
 * A bar is decoration for a number that is already written out beside it, so a
 * zero denominator answers zero rather than throwing.
 */
export function shareOf(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.min(100, Math.max(0, (part / whole) * 100));
}

/* -------------------------------------------------------------------------- */
/* What kind of segment it is — slice 50                                       */
/* -------------------------------------------------------------------------- */

/**
 * The chip word for a cohort.
 *
 * **"Snapshot", and it is the fix for a zero that reads as an omission.** A
 * cohort combines no uploads, so `datasetCount` is `0` and `/segments` draws the
 * lowercase mono "none" in its Lists column. That zero is real and the contract
 * insists on it — but read on its own it says *somebody forgot to put lists in
 * this one*, which is the opposite of what it means. The chip is what turns it
 * into a fact about the kind: a segment marked `Snapshot` combines no lists
 * because it is not a union, it is who was counted on one day.
 *
 * The word is not `OPPORTUNITY_COPY`'s and could not be: that vocabulary names
 * the COUNT a cohort came from — "Dormant audience" — and a chip reading
 * "Dormant audience" beside a segment called "Dormant audience · 26 Sep 2026"
 * would have said one thing twice and the thing a reader needs neither time.
 * It is not `type` off the wire either: `cohort` is a word for a schema, and
 * nobody sending a campaign has read the schema.
 *
 * "Static" has deliberately NO chip. Slice 29 deleted a "Static union" badge
 * for distinguishing a segment from nothing, and re-introducing it to balance
 * this one would repeat that: the absence of the chip is the other state, and a
 * screen where every row wears a badge has no badge. Slice 66's design spec
 * asked for a "Static" chip in the same `ident-a` as this one; it was left out
 * on exactly this ground — and two kinds in one colour would have made the
 * colour say nothing either — and is recorded in SLICES.md as the owner's call
 * to reverse, not an oversight.
 */
export const COHORT_CHIP = 'Snapshot';

/**
 * Everything a screen says about where a cohort came from: three labels, no
 * sentence.
 */
export interface CohortProvenance {
  /**
   * The chip beside the name — `Badge tone="ident-a"` since slice 66, an
   * identity colour rather than neutral grey: neutral is the tone of "nothing
   * to report", and a kind is something to report, but not a status.
   */
  readonly chip: string;
  /** Which count produced it, in the count's own words. */
  readonly kind: string;
  /** When it was counted — `counted 26 Sep 2026`. */
  readonly countedAt: string;
}

/**
 * A cohort's provenance as the labels that draw it, or `null` for a static
 * segment, which has none to draw.
 *
 * `null` in and `null` out rather than a caller branching on `type`: a static
 * segment's `cohort` is `null` by contract and in both directions — the database
 * refuses a cohort without a kind and a static segment carrying one — so this
 * one test cannot disagree with the other field.
 *
 * **The date is labelled "counted", and that word is the whole point of putting
 * it on screen.** The detail screen's meta line already carries `created` and
 * `updated`; a fourth bare date beside them would be a date whose subject the
 * reader has to guess, and the one thing they cannot guess is the only thing
 * that matters — a cohort is a snapshot, and a three-month-old one is a list of
 * who was dormant three months ago. `formatDate` renders it in the reader's own
 * zone, unlike `defaultCohortName`'s UTC, and the difference is deliberate: that
 * name is STORED and must read the same to everyone, while this is drawn now,
 * for the person looking at it.
 */
export function cohortProvenance(cohort: SegmentCohort | null): CohortProvenance | null {
  if (cohort === null) return null;

  return {
    chip: COHORT_CHIP,
    kind: OPPORTUNITY_COPY[cohort.kind].label,
    countedAt: `counted ${formatDate(cohort.countedAt)}`,
  };
}
